/**
 * BulkActionsBar — sticky bar that floats at the bottom of the list when one
 * or more candidates are selected. Provides:
 *   • "Mettre au vivier" (opens the same reason picker as the single-card flow)
 *   • "Télécharger badges (ZIP)" (iter110d · CEO 2026-09-16 · print-day batch)
 *   • "Tout désélectionner"
 *
 * Used by the "Mes appels" / Candidates list page when `selectable` mode is on.
 */
import { motion } from "framer-motion";
import { Bookmark, BadgeCheck, FileText, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";


export default function BulkActionsBar({ selectedIds, onClear, onDone }) {
    const [showModal, setShowModal] = useState(false);
    const [reasons, setReasons] = useState({});
    const [reason, setReason] = useState("cant_reach");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [downloadingBadges, setDownloadingBadges] = useState(false);
    const [downloadingCvs, setDownloadingCvs] = useState(false);

    useEffect(() => {
        // Load reasons once — same dict the single-card modal uses.
        api.get("/candidates/talent-pool/reasons")
            .then((r) => setReasons(r.data?.reasons || {}))
            .catch(() => { /* keep empty */ });
    }, []);

    if (selectedIds.size === 0) return null;
    const count = selectedIds.size;

    async function submit() {
        if (!reason) {
            toast.error("Sélectionnez une raison");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/candidates/talent-pool/bulk", {
                ids: Array.from(selectedIds),
                reason,
                note: note.trim() || null,
            });
            const { counts } = data;
            const movedN = counts?.moved ?? 0;
            const skippedN = counts?.skipped ?? 0;
            toast.success(
                `${movedN} candidat${movedN > 1 ? "s" : ""} au vivier ✅`,
                {
                    description: skippedN
                        ? `${skippedN} déjà au vivier, ignoré${skippedN > 1 ? "s" : ""}.`
                        : (reasons[reason] || reason),
                    duration: 5000,
                },
            );
            window.dispatchEvent(new CustomEvent("vivier:changed"));
            onDone?.(data.moved || []);
            setShowModal(false);
            setNote("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Action impossible");
        } finally {
            setSubmitting(false);
        }
    }

    async function downloadBadges() {
        setDownloadingBadges(true);
        try {
            const response = await api.post(
                "/candidates/badges/bulk",
                { candidate_ids: Array.from(selectedIds) },
                { responseType: "blob" },
            );
            const okN = parseInt(response.headers["x-badges-ok"] || "0", 10);
            const errN = parseInt(response.headers["x-badges-errors"] || "0", 10);
            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: "application/zip" }),
            );
            const link = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 10);
            link.href = url;
            link.download = `badges_shariti_${stamp}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            if (okN > 0) {
                toast.success(
                    `${okN} badge${okN > 1 ? "s" : ""} téléchargé${okN > 1 ? "s" : ""}`,
                    {
                        description: errN
                            ? `${errN} candidat${errN > 1 ? "s" : ""} ignoré${errN > 1 ? "s" : ""} (voir _erreurs.txt dans le ZIP)`
                            : "Impression duplex A4 → 4 badges par feuille.",
                        duration: 6000,
                    },
                );
            } else {
                toast.error(
                    "Aucun badge généré",
                    { description: `${errN} candidat${errN > 1 ? "s" : ""} sans prérequis (voir _erreurs.txt)` },
                );
            }
        } catch (e) {
            let detail = "Impossible de générer les badges";
            if (e.response?.data instanceof Blob) {
                try { detail = JSON.parse(await e.response.data.text()).detail || detail; }
                catch { /* silent */ }
            } else {
                detail = e.response?.data?.detail || e.message || detail;
            }
            toast.error(detail);
        } finally {
            setDownloadingBadges(false);
        }
    }

    async function downloadCvs() {
        setDownloadingCvs(true);
        try {
            const response = await api.post(
                "/candidates/cv/bulk",
                { candidate_ids: Array.from(selectedIds) },
                {
                    responseType: "blob",
                    // 300s : downloads Layan CDN peuvent prendre ~20-40s
                    // pour 100+ CVs en parallèle. Global axios timeout
                    // (60s) est trop court pour ce cas.
                    timeout: 300000,
                },
            );
            const okN = parseInt(response.headers["x-cvs-ok"] || "0", 10);
            const errN = parseInt(response.headers["x-cvs-errors"] || "0", 10);
            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: "application/zip" }),
            );
            const link = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 10);
            link.href = url;
            link.download = `cvs_shariti_${stamp}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            if (okN > 0) {
                toast.success(
                    `${okN} CV${okN > 1 ? "s" : ""} téléchargé${okN > 1 ? "s" : ""}`,
                    {
                        description: errN
                            ? `${errN} candidat${errN > 1 ? "s" : ""} sans CV ou hors périmètre (voir _erreurs.txt dans le ZIP)`
                            : `Archive : cvs_shariti_${stamp}.zip`,
                        duration: 6000,
                    },
                );
            } else {
                toast.error(
                    "Aucun CV téléchargé",
                    { description: `${errN} candidat${errN > 1 ? "s" : ""} sans CV disponible (voir _erreurs.txt)` },
                );
            }
        } catch (e) {
            let detail = "Impossible de télécharger les CVs";
            if (e.response?.data instanceof Blob) {
                try { detail = JSON.parse(await e.response.data.text()).detail || detail; }
                catch { /* silent */ }
            } else {
                detail = e.response?.data?.detail || e.message || detail;
            }
            toast.error(detail);
        } finally {
            setDownloadingCvs(false);
        }
    }

    return (
        <>
            <motion.div
                initial={{ y: 60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 60, opacity: 0 }}
                className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 w-[min(760px,calc(100%-32px))]"
                data-testid="bulk-actions-bar"
            >
                <div className="rounded-2xl bg-shariti-black text-white shadow-2xl border border-shariti-orange/30 px-4 md:px-5 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-xl bg-shariti-orange text-white inline-flex items-center justify-center shrink-0 font-extrabold text-sm" data-testid="bulk-count-badge">
                            {count}
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm font-extrabold leading-tight">
                                {count} candidat{count > 1 ? "s" : ""} sélectionné{count > 1 ? "s" : ""}
                            </div>
                            <div className="text-[11px] text-white/60 leading-tight mt-0.5">
                                Action groupée disponible
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={downloadCvs}
                            disabled={downloadingCvs}
                            data-testid="bulk-cvs-btn"
                            title="Télécharger les CVs des candidats sélectionnés en un seul ZIP"
                            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-white px-4 h-9 font-extrabold text-xs hover:bg-white/20 transition disabled:opacity-60"
                        >
                            {downloadingCvs
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <FileText className="h-3.5 w-3.5" />}
                            {downloadingCvs ? "Génération…" : "CVs ZIP"}
                        </button>
                        <button
                            type="button"
                            onClick={downloadBadges}
                            disabled={downloadingBadges}
                            data-testid="bulk-badges-btn"
                            title="Télécharger les badges des collaborateurs éligibles"
                            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-white px-4 h-9 font-extrabold text-xs hover:bg-white/20 transition disabled:opacity-60"
                        >
                            {downloadingBadges
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <BadgeCheck className="h-3.5 w-3.5" />}
                            {downloadingBadges ? "Génération…" : "Badges ZIP"}
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowModal(true)}
                            data-testid="bulk-vivier-btn"
                            className="inline-flex items-center gap-1.5 rounded-full bg-shariti-orange text-white px-4 h-9 font-extrabold text-xs hover:bg-[#d66528] transition shadow-btn"
                        >
                            <Bookmark className="h-3.5 w-3.5" />
                            Vivier
                        </button>
                        <button
                            type="button"
                            onClick={onClear}
                            data-testid="bulk-clear-btn"
                            title="Tout désélectionner"
                            className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </motion.div>

            {showModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={() => !submitting && setShowModal(false)}
                    data-testid="bulk-vivier-modal"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
                    >
                        <div className="px-5 py-4 border-b border-shariti-gray flex items-center justify-between">
                            <div>
                                <div className="text-[10px] font-extrabold uppercase tracking-widest text-amber-700">
                                    Action groupée
                                </div>
                                <div className="text-sm font-extrabold text-shariti-black mt-0.5">
                                    Envoyer {count} candidat{count > 1 ? "s" : ""} au vivier
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => !submitting && setShowModal(false)}
                                className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-red hover:bg-red-50 transition"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="p-5">
                            <label className="block text-xs font-extrabold uppercase tracking-widest text-slate-600 mb-2">
                                Raison commune <span className="text-shariti-red">*</span>
                            </label>
                            <select
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                data-testid="bulk-vivier-reason"
                                className="w-full rounded-xl border-2 border-shariti-gray px-3 h-11 text-sm font-bold focus:outline-none focus:border-shariti-orange transition mb-4"
                            >
                                {Object.entries(reasons).map(([k, v]) => (
                                    <option key={k} value={k}>{v}</option>
                                ))}
                            </select>
                            <label className="block text-xs font-extrabold uppercase tracking-widest text-slate-600 mb-2">
                                Note (optionnelle)
                            </label>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                data-testid="bulk-vivier-note"
                                rows={3}
                                placeholder="Ex: Lot ajouté en attente de l'ouverture d'une nouvelle mission"
                                className="w-full rounded-xl border-2 border-shariti-gray px-3 py-2 text-sm focus:outline-none focus:border-shariti-orange transition resize-none"
                            />
                        </div>
                        <div className="px-5 py-4 border-t border-shariti-gray flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                disabled={submitting}
                                className="px-4 h-10 rounded-full text-sm font-bold text-slate-600 hover:bg-shariti-cream transition disabled:opacity-50"
                            >
                                Annuler
                            </button>
                            <button
                                type="button"
                                onClick={submit}
                                disabled={submitting || !reason}
                                data-testid="bulk-vivier-submit"
                                className="px-5 h-10 rounded-full text-sm font-extrabold text-white bg-shariti-orange hover:bg-[#d66528] shadow-btn transition inline-flex items-center gap-2 disabled:opacity-50"
                            >
                                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                Confirmer · {count} candidat{count > 1 ? "s" : ""}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </>
    );
}
